/* eslint-disable no-console */
import { CHATKIT_CONFIGS } from './configs';
import { extractContextFromDataset } from '../common/helpers';
import { loadChatkitScript } from './scriptLoader';
import { updateChatkitConversation, fetchToken } from './apiRoutes';
import * as crossTabSync from '../common/crossTabSync';

const widgets = new Map();

let flowType = null;
let chatkitThreadId = null;

function _getConfig(container) {
  const type = container.dataset.chatkitType || flowType;
  const config = { ...CHATKIT_CONFIGS[type] };

  const dynamicContext = extractContextFromDataset(container);
  if (Object.keys(dynamicContext).length > 0) {
    config.context = { ...config.context, ...dynamicContext };
  }

  return config;
}

async function _prepareConfigAndToken(container) {
  if (container.dataset.chatkitType) {
    const config = _getConfig(container);
    const tokenData = await fetchToken(chatkitThreadId, config.context);
    return { config, tokenData };
  }

  const dynamicContext = extractContextFromDataset(container);
  const tokenData = await fetchToken(chatkitThreadId, dynamicContext);
  if (!tokenData) return null;

  flowType = tokenData.flowType;
  const config = _getConfig(container);
  return { config, tokenData };
}

export async function initializeOpenAIWidget(container, initialOptions = {}) {
  if (initialOptions.initialThread) chatkitThreadId = initialOptions.initialThread;
  await loadChatkitScript();

  const kit = container.querySelector('openai-chatkit');
  if (!kit) return;

  const widgetId = container.id;
  if (widgets.has(widgetId) && widgets.get(widgetId).isInitialized) {
    return;
  }

  if (kit.shadowRoot && kit.shadowRoot.querySelector('iframe')) {
    widgets.set(widgetId, { isInitialized: true });
    return;
  }

  const result = await _prepareConfigAndToken(container);
  const { config, tokenData } = result;

  try {
    const userSettings = document.querySelector('[data-controller="user-account-settings"]');
    const appearanceMode = userSettings?.dataset?.userAccountSettingsThemeValue || 'light';

    const options = {
      api: {
        async getClientSecret(refreshSession) {
          if (refreshSession) {
            const newTokenData = await fetchToken(chatkitThreadId, config.context);
            if (newTokenData) return newTokenData.token;
            throw new Error('Failed to refresh ChatKit token');
          }
          return tokenData.token;
        }
      },
      initialThread: chatkitThreadId,
      theme: {
        colorScheme: appearanceMode
      },
      composer: {
        placeholder: config.placeholder,
        attachments: config.attachments
      },
      startScreen: {
        greeting: config.greeting,
        prompts: config.prompts
      },
      ...initialOptions
    };

    kit.setOptions(options);
    crossTabSync.setup();

    kit.addEventListener('chatkit.response.start', () => {
      document.dispatchEvent(new CustomEvent('chatkit.response.start', { bubbles: true }));
      crossTabSync.broadcast('chatkit.sync.response.start');
    });

    kit.addEventListener('chatkit.response.end', () => {
      document.dispatchEvent(new CustomEvent('chatkit.response.end', { bubbles: true }));
      crossTabSync.broadcast('chatkit.sync.response.end');
    });

    kit.addEventListener('chatkit.thread.change', async (event) => {
      chatkitThreadId = event.detail.threadId;
      await updateChatkitConversation(chatkitThreadId, flowType);
      crossTabSync.broadcast('chatkit.sync.thread.change', { threadId: chatkitThreadId });
    });

    document.addEventListener('chatkit.end-chat', () => {
      chatkitThreadId = null;
      kit.setThreadId(null);
    });

    document.addEventListener('chatkit.sync.response.start', () => {
      setTimeout(() => { if (kit.fetchUpdates) kit.fetchUpdates(); }, 200);
    });

    document.addEventListener('chatkit.sync.response.end', () => {
      setTimeout(() => { if (kit.fetchUpdates) kit.fetchUpdates(); }, 1000);
    });

    document.addEventListener('chatkit.sync.thread.change', (event) => {
      const { threadId } = event.detail;
      chatkitThreadId = threadId;
      kit.setThreadId(threadId);
    });

    widgets.set(widgetId, { isInitialized: true, config });
  } catch (error) {
    console.error('Error initializing ChatKit:', error);
  }
}

export function destroyOpenAIWidget(widgetId) {
  const container = document.getElementById(widgetId);
  if (!container) return;

  const kit = container.querySelector('openai-chatkit');
  if (kit && kit.shadowRoot) {
    kit.shadowRoot.innerHTML = '';
  }

  widgets.delete(widgetId);
}

export function destroyAllOpenAIWidgets() {
  widgets.forEach((_, widgetId) => {
    destroyOpenAIWidget(widgetId);
  });
  crossTabSync.cleanup();
}
